import React, { useState, useEffect, useMemo } from 'react';
import {
  Building2,
  UserCheck,
  FileSpreadsheet,
  Search,
  ChevronDown,
  ChevronUp,
  Phone,
  RefreshCw,
  Layers,
  AlertCircle,
  User,
  Globe,
  Users,
  Briefcase
} from 'lucide-react';

const EInvoicingDashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  // Fetch API Data
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        'https://cubixweberp.com:406/api/ContactEntity/get-contact-entities?compcode=CUBIX'
      );
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }
      const json = await response.json();
      setData(json);
    } catch (err) {
      setError(err.message || 'Failed to load records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Compute Metrics for Dashboard Top Bar
  const metrics = useMemo(() => {
    let totalContacts = data.length;
    let totalEntities = 0;
    let totalSalesInvoices = 0;
    let totalPurchaseInvoices = 0;
    let totalB2B = 0;
    let totalB2C = 0;
    let totalExport = 0;

    data.forEach((contact) => {
      if (Array.isArray(contact.entities)) {
        totalEntities += contact.entities.length;
        contact.entities.forEach((entity) => {
          totalSalesInvoices += entity.salesInvoiceMetrics?.totalPerAnnum || 0;
          totalPurchaseInvoices += entity.operationalMetrics?.purchaseInvoicesPerAnnum || 0;

          totalB2B += entity.salesInvoiceMetrics?.breakdown?.b2b || 0;
          totalB2C += entity.salesInvoiceMetrics?.breakdown?.b2g || 0;
          totalExport += entity.salesInvoiceMetrics?.breakdown?.export || 0;
        });
      }
    });

    return {
      totalContacts,
      totalEntities,
      totalSalesInvoices,
      totalPurchaseInvoices,
      totalB2B,
      totalB2C,
      totalExport,
    };
  }, [data]);

  // Search/Filter across Contact Name, Phone, or Any Entity Name
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;

    const term = searchTerm.toLowerCase();
    return data.filter((item) => {
      const contactMatch = item.contactName?.toLowerCase().includes(term);
      const phoneMatch = item.phoneNumber?.toLowerCase().includes(term);
      const entityMatch = item.entities?.some((e) =>
        e.entityName?.toLowerCase().includes(term)
      );

      return contactMatch || phoneMatch || entityMatch;
    });
  }, [data, searchTerm]);

  const toggleRow = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  return (
    <div className="bg-slate-50 p-4 text-slate-800">
      <div className="mx-auto space-y-6 ">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              UAE E-Invoicing Registrations
            </h1>
            <p className="text-sm text-slate-500">
              Overview of registered contacts with detailed B2B, B2C, and Export breakdowns.
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-80">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by contact, phone, or company name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition shadow-sm"
              />
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition disabled:opacity-50 text-sm font-medium shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Top Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Total Contacts / Companies
              </p>
              <h3 className="text-2xl font-extrabold text-slate-900 mt-1">
                {metrics.totalContacts} <span className="text-sm text-slate-400 font-normal">({metrics.totalEntities} entities)</span>
              </h3>
            </div>
            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
              <UserCheck className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Total B2B Volume
              </p>
              <h3 className="text-2xl font-extrabold text-blue-600 mt-1">
                {metrics.totalB2B.toLocaleString()}
              </h3>
            </div>
            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
              <Briefcase className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Total B2C Volume
              </p>
              <h3 className="text-2xl font-extrabold text-purple-600 mt-1">
                {metrics.totalB2C.toLocaleString()}
              </h3>
            </div>
            <div className="p-3 bg-purple-50 text-purple-600 rounded-lg">
              <Users className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Total Export Volume
              </p>
              <h3 className="text-2xl font-extrabold text-emerald-600 mt-1">
                {metrics.totalExport.toLocaleString()}
              </h3>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
              <Globe className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Main Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500 font-medium">
              Loading e-invoicing registrations...
            </div>
          ) : error ? (
            <div className="p-8 text-center text-rose-500 flex flex-col items-center gap-2">
              <AlertCircle className="w-8 h-8" />
              <span>Failed to load data: {error}</span>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              No matching client records found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="py-3 px-4 w-8"></th>
                    <th className="py-3 px-4 min-w-[160px]">Contact Person</th>
                    <th className="py-3 px-4 min-w-[130px]">Phone Number</th>
                    <th className="py-3 px-4 min-w-[200px]">Companies / Entities</th>
                    <th className="py-3 px-4 min-w-[220px]">Breakdown (B2B / B2C / Export)</th>
                    <th className="py-3 px-4 text-right min-w-[120px]">Total Sales</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredData.map((item, idx) => {
                    const isExpanded = expandedRow === idx;
                    const entities = item.entities || [];

                    // Aggregate totals across all entities under this contact
                    let b2bTotal = 0;
                    let b2cTotal = 0;
                    let exportTotal = 0;
                    let totalSales = 0;

                    entities.forEach((entity) => {
                      totalSales += entity.salesInvoiceMetrics?.totalPerAnnum || 0;
                      b2bTotal += entity.salesInvoiceMetrics?.breakdown?.b2b || 0;
                      b2cTotal += entity.salesInvoiceMetrics?.breakdown?.b2g || 0;
                      exportTotal += entity.salesInvoiceMetrics?.breakdown?.export || 0;
                    });

                    return (
                      <React.Fragment key={idx}>
                        <tr
                          onClick={() => toggleRow(idx)}
                          className="hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <td className="py-3 px-4 text-slate-400">
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-blue-600" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </td>

                          {/* Contact Person */}
                          <td className="py-3 px-4 font-bold text-slate-900">
                            <span className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400 shrink-0" />
                              {item.contactName || 'N/A'}
                            </span>
                          </td>

                          {/* Phone */}
                          <td className="py-3 px-4 text-slate-600">
                            <span className="inline-flex items-center gap-1.5">
                              <Phone className="w-3.5 h-3.5 text-slate-400" />
                              {item.phoneNumber || '—'}
                            </span>
                          </td>

                          {/* Inline List of Companies */}
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1.5">
                              {entities.length > 0 ? (
                                entities.map((entity, eIdx) => (
                                  <span
                                    key={eIdx}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200"
                                  >
                                    <Building2 className="w-3 h-3 text-slate-500" />
                                    {entity.entityName}
                                  </span>
                                ))
                              ) : (
                                <span className="text-slate-400 text-xs italic">
                                  No entities assigned
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Inline Breakdown Badges */}
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2 text-xs">
                              <span className="px-2 py-0.5 rounded font-semibold bg-blue-50 text-blue-700 border border-blue-200" title="B2B Volume">
                                B2B: {b2bTotal.toLocaleString()}
                              </span>
                              <span className="px-2 py-0.5 rounded font-semibold bg-purple-50 text-purple-700 border border-purple-200" title="B2C Volume">
                                B2C: {b2cTotal.toLocaleString()}
                              </span>
                              <span className="px-2 py-0.5 rounded font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" title="Export Volume">
                                Exp: {exportTotal.toLocaleString()}
                              </span>
                            </div>
                          </td>

                          {/* Aggregated Total Sales */}
                          <td className="py-3 px-4 text-right font-bold text-slate-900">
                            {totalSales.toLocaleString()}
                          </td>
                        </tr>

                        {/* Expanded Section with Company-by-Company Breakdown Cards */}
                        {isExpanded && (
                          <tr className="bg-slate-50/80">
                            <td colSpan="6" className="p-4 border-inner">
                              <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4 shadow-inner">
                                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-blue-600" />
                                    Detailed Breakdown for Entities Under {item.contactName}
                                  </h4>
                                  <span className="text-xs text-slate-400">
                                    {entities.length} {entities.length === 1 ? 'Company' : 'Companies'}
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {entities.map((entity, eIdx) => {
                                    const b2b = entity.salesInvoiceMetrics?.breakdown?.b2b || 0;
                                    const b2c = entity.salesInvoiceMetrics?.breakdown?.b2g || 0;
                                    const exp = entity.salesInvoiceMetrics?.breakdown?.export || 0;
                                    const total = entity.salesInvoiceMetrics?.totalPerAnnum || 0;

                                    return (
                                      <div
                                        key={eIdx}
                                        className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-3"
                                      >
                                        <div className="flex justify-between items-start gap-2">
                                          <h5 className="font-bold text-slate-900 text-sm">
                                            {entity.entityName}
                                          </h5>
                                          {entity.vendorPortal?.hasPortal && (
                                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 shrink-0">
                                              Portal Active
                                            </span>
                                          )}
                                        </div>

                                        {/* Company Specific Breakdown */}
                                        <div className="space-y-2 pt-1">
                                          <span className="text-slate-400 block text-[10px] uppercase font-bold">
                                            Sales Breakdown (Total: {total.toLocaleString()})
                                          </span>

                                          <div className="grid grid-cols-3 gap-2 text-xs">
                                            <div className="bg-blue-50/60 p-2 rounded border border-blue-100 text-center">
                                              <span className="block text-[10px] font-bold text-blue-600 uppercase">
                                                B2B
                                              </span>
                                              <span className="font-bold text-blue-900">
                                                {b2b.toLocaleString()}
                                              </span>
                                            </div>

                                            <div className="bg-purple-50/60 p-2 rounded border border-purple-100 text-center">
                                              <span className="block text-[10px] font-bold text-purple-600 uppercase">
                                                B2C
                                              </span>
                                              <span className="font-bold text-purple-900">
                                                {b2c.toLocaleString()}
                                              </span>
                                            </div>

                                            <div className="bg-emerald-50/60 p-2 rounded border border-emerald-100 text-center">
                                              <span className="block text-[10px] font-bold text-emerald-600 uppercase">
                                                Export
                                              </span>
                                              <span className="font-bold text-emerald-900">
                                                {exp.toLocaleString()}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Operations & Software Info */}
                                        <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-slate-200/60">
                                          <div>
                                            <span className="text-slate-400 text-[10px] uppercase font-bold block">
                                              Purchases / Yr
                                            </span>
                                            <span className="font-semibold text-slate-800">
                                              {(entity.operationalMetrics?.purchaseInvoicesPerAnnum || 0).toLocaleString()}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-slate-400 text-[10px] uppercase font-bold block">
                                              System / Software
                                            </span>
                                            <span className="font-semibold text-slate-800 truncate block">
                                              {entity.vendorPortal?.nameAndVersion || 'N/A'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default EInvoicingDashboard;